import { useEffect, useState, useCallback, useRef } from 'react';
import {
  useGetEpisodesForSeasonQuery,
  useGetSeasonsForSeriesQuery,
} from '#types/generated/apollo-client';
import SeasonSwitch from '#modules/series-details/SeasonsSwitch';
import {
  Media,
  ProductAccessReason,
  Season,
} from '#types/generated/graphql-types';
import useViewport from '#hooks/useViewport';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import EpisodeItemSmall from '#modules/series-details/EpisodeItemSmall';
import EpisodeItemLarge from '#modules/series-details/EpisodeItemLarge';
import { useLoginNotificationDialog } from '#modules/dialog-login-notification/DialogLoginNotification';
import { useRentNotificationDialog } from '#modules/dialog-rent-notification/DialogRentNotification';
import PageSection from '#components/sections/page-section/PageSection';
import CircularProgress from '#components/feedback/circular-progress/CircularProgress';
import { triggerPlayerPlay } from '#utils/player/controls';
import { useRouter } from 'next/router';
import useEpisodeUrl from '#hooks/useEpisodeUrl';
import videojs from 'video.js';
import getConfigVars from '#utils/get-config-vars';

const logger = videojs.log.createLogger('EpisodeExplorerV2');
logger.level(getConfigVars().LOG_LEVEL);

type Props = {
  seriesId: number;
};

const EpisodeExplorer = ({ seriesId }: Props) => {
  useEffect(() => {
    // TODO: remove before prod
    logger('[MOUNT] EpisodeExplorer.v2 mounted', { seriesId });
    return () => {
      logger('[UNMOUNT] EpisodeExplorer.v2 unmounted', { seriesId });
    };
  }, [seriesId]);
  const router = useRouter();
  const { clearEpisodeFromUrl, setEpisodeInUrl } = useEpisodeUrl();

  const { initPlayerV2, setOnNextEpisodeCallback } =
    useOvermindActions().playerV2;
  const { itemMap } = useOvermindState().continueWatching;

  const { isSmall } = useViewport();
  const { showLoginNotificationDialog } = useLoginNotificationDialog();
  const { showRentNotificationDialog } = useRentNotificationDialog();
  const {
    user: { profile },
  } = useOvermindState();
  const [selectedSeason, setSelectedSeason] = useState<Season | undefined>();

  const { data: series, loading: seasonsLoading } = useGetSeasonsForSeriesQuery(
    {
      ssr: false,
      variables: {
        seriesId,
      },
    }
  );

  const { data: season, loading: episodesLoading } =
    useGetEpisodesForSeasonQuery({
      ssr: false,
      variables: {
        seasonId: selectedSeason?.id,
      },
      skip: !selectedSeason,
    });

  const episodeIdFromUrl = router.query.id ? Number(router.query.id) : null;

  const seasonRef = useRef(season);
  const episodeIdRef = useRef(episodeIdFromUrl);

  useEffect(() => {
    seasonRef.current = season;
    episodeIdRef.current = episodeIdFromUrl;
  }, [season, episodeIdFromUrl]);

  const moveToNextEpisode = useCallback(() => {
    const currentSeasonData = seasonRef.current;
    const currentEpisodeId = episodeIdRef.current;

    if (!currentSeasonData?.seasonById?.episodes || !currentEpisodeId) return;

    const episodes = currentSeasonData.seasonById.episodes;
    const currentIndex = episodes.findIndex((ep) => ep.id === currentEpisodeId);

    if (currentIndex === -1 || currentIndex === episodes.length - 1) return;

    const nextEpisode = episodes[currentIndex + 1];

    if (nextEpisode) {
      setEpisodeInUrl(nextEpisode.id);
    }
  }, [setEpisodeInUrl]);

  useEffect(() => {
    setOnNextEpisodeCallback(moveToNextEpisode);

    return () => {
      setOnNextEpisodeCallback(null);
    };
  }, [setOnNextEpisodeCallback, moveToNextEpisode]);

  useEffect(() => {
    if (series?.seriesById?.seasons) {
      setSelectedSeason(series.seriesById.seasons[0]);
    }
  }, [series]);

  const handleSeasonChange = (seasonId) => {
    const newSelectedSeason = series?.seriesById?.seasons.find(
      (item) => item.id === seasonId
    );
    clearEpisodeFromUrl();
    setSelectedSeason(newSelectedSeason);
  };

  useEffect(() => {
    if (!episodeIdFromUrl) return;
    if (!season?.seasonById?.episodes) return;

    const episode = season.seasonById.episodes.find(
      (ep) => ep.id === episodeIdFromUrl
    );

    if (!episode) return;

    // 🔐 Access checks stay here (not in click)
    const access = episode?.products?.access;

    if (
      access?.allowed === false &&
      access?.reason === ProductAccessReason.LoginRequired &&
      !profile
    ) {
      showLoginNotificationDialog();
      return;
    }

    const episodes = season.seasonById.episodes.map((ep) => ({
      episodeIri: ep.iri,
      episodeNumber: ep.episodeNumber,
      id: ep.id,
      seasonIri: season.seasonById.id.toString(),
      seasonNumber: season.seasonById.id,
    }));

    initPlayerV2({
      iri: episode.iri,
      contentType: episode.contentType,
      series: {
        episodes,
      },
    });

    triggerPlayerPlay();
  }, [
    episodeIdFromUrl,
    season,
    profile,
    initPlayerV2,
    showLoginNotificationDialog,
    triggerPlayerPlay,
  ]);

  const handlePlayButtonClick = (item: Media) => {
    //todo - use common logic for triggering play button click @see HeroButtonParade | SeriesHeroButtonParade - marius.iliescu
    setEpisodeInUrl(item.id);
  };

  const handleProductButtonClick = (mediaIri, productIri) => {
    if (!profile) {
      showLoginNotificationDialog('purchase');
      return;
    }

    showRentNotificationDialog(mediaIri, productIri);
  };

  if (seasonsLoading || !series || !selectedSeason) {
    return (
      <PageSection>
        <div className="tw-flex tw-items-center tw-justify-center">
          <CircularProgress />
        </div>
      </PageSection>
    );
  }

  return (
    <div>
      <SeasonSwitch
        classes="tw-mb-8"
        items={series?.seriesById?.seasons}
        selectedId={selectedSeason.id}
        onChange={handleSeasonChange}
        hideSeasonNumbers={series?.seriesById?.hideSeasonNumbers}
      />
      {(episodesLoading || !season) && (
        <PageSection>
          <div className="tw-flex tw-items-center tw-justify-center">
            <CircularProgress />
          </div>
        </PageSection>
      )}
      <div>
        {season?.seasonById?.episodes?.map((item) => {
          const Component = isSmall ? EpisodeItemSmall : EpisodeItemLarge;
          return (
            <Component
              continueWatchingState={
                !!profile && itemMap[item.iri] ? itemMap[item.iri] : null
              }
              key={item.id}
              item={item}
              onPlayButtonClick={handlePlayButtonClick}
              onProductButtonClick={handleProductButtonClick}
            />
          );
        })}
      </div>
    </div>
  );
};

export default EpisodeExplorer;
